import type {
  Agent,
  ApiProblem,
  DailySalesSeries,
  DetectResult,
  FundingOrder,
  Order,
  OrderSummary,
  Paginated,
  Statement,
  User,
  Wallet,
} from "@evocha/shared-types";

export interface ClientOptions {
  baseUrl: string;
  /** Access token, kept in memory. Never persisted by this module. */
  getToken?: () => string | null;
  /** Refresh token, used once when an authenticated request returns 401. */
  getRefreshToken?: () => string | null;
  /** Called after a successful token rotation so the app can store the pair. */
  onTokensRefreshed?: (tokens: TokenPair) => void;
  /** Called once when a request cannot be authorised (refresh failed too). */
  onUnauthorized?: () => void;
  /** Refresh endpoint path. Defaults to the agent one. */
  refreshPath?: string;
  fetchImpl?: typeof fetch;
  /** Extra headers (device id, app version, language). */
  headers?: () => Record<string, string>;
  timeoutMs?: number;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_in: number;
  refresh_expires_in?: number;
}

export interface AuthSession extends TokenPair {
  user: User;
  agent: Agent;
}

export interface VerifyOtpResult {
  next: "pin" | "set_pin";
  device_challenge: string;
  registered: boolean;
}

export interface Me {
  user: User;
  agent: Agent;
  wallet: Wallet | null;
  permissions: string[];
}

export interface RegisterInput {
  msisdn: string;
  first_name?: string;
  last_name?: string;
  business_name: string;
  region: string;
  pin: string;
}

export class ApiError extends Error {
  readonly problem: ApiProblem;
  readonly status: number;

  constructor(problem: ApiProblem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
    this.status = problem.status;
  }

  get code(): string {
    return this.problem.code;
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export interface StatementParams {
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface OrdersParams {
  status?: string;
  mno?: string;
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `idem_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.replace(/^\//, ""), base.endsWith("/") ? base : `${base}/`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export class EvochaClient {
  private readonly opts: ClientOptions;
  private readonly fetchImpl: typeof fetch;
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(opts: ClientOptions) {
    this.opts = opts;
    // Browsers throw "Illegal invocation" if window.fetch is called with a
    // different receiver, so always invoke it through a neutral wrapper.
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private headers(body: unknown, options: RequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Accept-Language": "en",
      ...(this.opts.headers?.() ?? {}),
    };
    const token = this.opts.getToken?.() ?? null;
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    return headers;
  }

  private async send(
    method: string,
    path: string,
    body: unknown,
    options: RequestOptions,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.opts.timeoutMs ?? 20_000);
    try {
      return await this.fetchImpl(buildUrl(this.opts.baseUrl, path, options.query), {
        method,
        headers: this.headers(body, options),
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal ?? controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Rotate the refresh token once. The API accepts the refresh token in the
   * Authorization header for this endpoint, so it cannot go through `send`.
   */
  private refresh(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const refreshToken = this.opts.getRefreshToken?.() ?? null;
    if (!refreshToken) return Promise.resolve(false);

    this.refreshInFlight = (async () => {
      try {
        const res = await this.fetchImpl(buildUrl(this.opts.baseUrl, this.opts.refreshPath ?? "/v1/auth/refresh"), {
          method: "POST",
          headers: { Accept: "application/json", Authorization: `Bearer ${refreshToken}` },
        });
        if (!res.ok) return false;
        const payload = (await res.json()) as TokenPair;
        if (!payload?.access_token || !payload?.refresh_token) return false;
        this.opts.onTokensRefreshed?.(payload);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    let res = await this.send(method, path, body, options);

    // Auth calls (login, logout) return 401 for bad credentials; never try to
    // refresh those or bounce the app to the login screen.
    const isAuthCall = /\/auth\//.test(path);

    // Access tokens are short-lived (15 min). Refresh once, then replay.
    if (res.status === 401 && !isAuthCall) {
      const refreshed = await this.refresh();
      if (refreshed) res = await this.send(method, path, body, options);
    }

    if (res.status === 401 && !isAuthCall) {
      this.opts.onUnauthorized?.();
    }

    const text = await res.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        payload = null;
      }
    }

    if (!res.ok) {
      const problem = (payload ?? {}) as Partial<ApiProblem>;
      throw new ApiError({
        type: problem.type ?? "about:blank",
        title: problem.title ?? "Request failed",
        status: res.status,
        code: problem.code ?? `http_${res.status}`,
        detail: problem.detail,
        instance: problem.instance,
        trace_id: problem.trace_id,
        errors: problem.errors,
      });
    }

    return payload as T;
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("POST", path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", path, body, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", path, undefined, options);
  }

  /* ---- typed surface (subset used by the shells) ---- */

  requestOtp(msisdn: string, purpose: "login" | "enrol" = "login") {
    return this.post<{ challenge_id: string; expires_in: number; resend_after: number; channel: string }>(
      "/v1/auth/otp/request",
      { msisdn, purpose },
    );
  }

  verifyOtp(challengeId: string, code: string) {
    return this.post<VerifyOtpResult>("/v1/auth/otp/verify", {
      challenge_id: challengeId,
      code,
    });
  }

  loginWithPin(deviceChallenge: string, pin: string, deviceId: string) {
    return this.post<AuthSession>("/v1/auth/pin", {
      device_challenge: deviceChallenge,
      pin,
      device_id: deviceId,
    });
  }

  register(input: RegisterInput) {
    return this.post<AuthSession>("/v1/auth/register", input);
  }

  logout() {
    return this.post<{ message: string }>("/v1/auth/logout", {
      refresh_token: this.opts.getRefreshToken?.() ?? undefined,
    });
  }

  me() {
    return this.get<Me>("/v1/me");
  }

  detect(msisdn: string) {
    return this.get<DetectResult>("/v1/providers/detect", { query: { msisdn } });
  }

  wallet() {
    return this.get<Wallet>("/v1/wallets/me");
  }

  statement(params: StatementParams = {}) {
    return this.get<Statement>("/v1/wallets/me/statement", {
      query: {
        from_date: params.from,
        to_date: params.to,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }

  createOrder(input: {
    msisdn: string;
    mno?: string;
    amount_minor: number | string;
    channel?: "mobile" | "web";
  }) {
    const amount = typeof input.amount_minor === "string" ? Number(input.amount_minor) : input.amount_minor;
    return this.post<Order>(
      "/v1/airtime/orders",
      {
        msisdn: input.msisdn,
        mno: input.mno,
        amount_minor: amount,
        channel: input.channel ?? "mobile",
        confirmed: true,
      },
      { idempotencyKey: newIdempotencyKey() },
    );
  }

  order(ref: string) {
    return this.get<Order>(`/v1/airtime/orders/${encodeURIComponent(ref)}`);
  }

  statusCheck(ref: string) {
    return this.post<Order>(`/v1/airtime/orders/${encodeURIComponent(ref)}/status-check`);
  }

  orders(params: OrdersParams = {}) {
    return this.get<Paginated<Order>>("/v1/airtime/orders", {
      query: {
        status: params.status,
        mno: params.mno,
        q: params.q,
        from_date: params.from,
        to_date: params.to,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }

  summary(period: "daily" | "weekly" | "monthly", date?: string) {
    return this.get<OrderSummary>("/v1/reports/agent-summary", { query: { period, date } });
  }

  dailySales(days = 7, date?: string) {
    return this.get<DailySalesSeries>("/v1/reports/daily-sales", { query: { days, date } });
  }

  createFunding(amountMinor: number | string) {
    return this.post<FundingOrder>(
      "/v1/funding/orders",
      { amount_minor: typeof amountMinor === "string" ? Number(amountMinor) : amountMinor },
      { idempotencyKey: newIdempotencyKey() },
    );
  }

  fundingOrder(id: string) {
    return this.get<FundingOrder>(`/v1/funding/orders/${encodeURIComponent(id)}`);
  }

  fundingOrders(limit = 20) {
    return this.get<{ items: FundingOrder[] }>("/v1/funding/orders", {
      query: { limit },
    });
  }
}

export type {
  Agent,
  ApiProblem,
  DailySalesSeries,
  DetectResult,
  FundingOrder,
  Order,
  OrderSummary,
  Paginated,
  Statement,
  User,
  Wallet,
};
